/* eslint-disable */
import { PluginContainerData, Media, Link } from './common.js';

import {
  GalleryOptions_Layout_Type,
  GalleryOptions_Layout_Orientation,
  GalleryOptions_ItemStyle_Crop,
  GalleryOptions_Thumbnails_Alignment,
} from './plugin_gallery.enums.js';

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptions_Layout;
  /** Styling for gallery items. */
  item?: GalleryOptions_ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: GalleryOptions_Thumbnails;
}

export interface GalleryOptions_Layout {
  /** Gallery layout type. */
  type: GalleryOptions_Layout_Type;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean;
  /** Gallery orientation. */
  orientation: GalleryOptions_Layout_Orientation;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number;
}

export function galleryOptions_Layout_TypeFromJSON(object: any): GalleryOptions_Layout_Type {
  switch (object) {
    case 0:
    case 'COLLAGE':
      return GalleryOptions_Layout_Type.COLLAGE;
    case 1:
    case 'MASONRY':
      return GalleryOptions_Layout_Type.MASONRY;
    case 2:
    case 'GRID':
      return GalleryOptions_Layout_Type.GRID;
    case 3:
    case 'THUMBNAIL':
      return GalleryOptions_Layout_Type.THUMBNAIL;
    case 4:
    case 'SLIDER':
      return GalleryOptions_Layout_Type.SLIDER;
    case 5:
    case 'SLIDESHOW':
      return GalleryOptions_Layout_Type.SLIDESHOW;
    case 6:
    case 'PANORAMA':
      return GalleryOptions_Layout_Type.PANORAMA;
    case 7:
    case 'COLUMN':
      return GalleryOptions_Layout_Type.COLUMN;
    case 8:
    case 'MAGIC':
      return GalleryOptions_Layout_Type.MAGIC;
    case 9:
    case 'FULLSIZE':
      return GalleryOptions_Layout_Type.FULLSIZE;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return GalleryOptions_Layout_Type.UNRECOGNIZED;
  }
}

export function galleryOptions_Layout_TypeToJSON(object: GalleryOptions_Layout_Type): string {
  switch (object) {
    case GalleryOptions_Layout_Type.COLLAGE:
      return 'COLLAGE';
    case GalleryOptions_Layout_Type.MASONRY:
      return 'MASONRY';
    case GalleryOptions_Layout_Type.GRID:
      return 'GRID';
    case GalleryOptions_Layout_Type.THUMBNAIL:
      return 'THUMBNAIL';
    case GalleryOptions_Layout_Type.SLIDER:
      return 'SLIDER';
    case GalleryOptions_Layout_Type.SLIDESHOW:
      return 'SLIDESHOW';
    case GalleryOptions_Layout_Type.PANORAMA:
      return 'PANORAMA';
    case GalleryOptions_Layout_Type.COLUMN:
      return 'COLUMN';
    case GalleryOptions_Layout_Type.MAGIC:
      return 'MAGIC';
    case GalleryOptions_Layout_Type.FULLSIZE:
      return 'FULLSIZE';
    default:
      return 'UNKNOWN';
  }
}

export function galleryOptions_Layout_OrientationFromJSON(
  object: any
): GalleryOptions_Layout_Orientation {
  switch (object) {
    case 0:
    case 'ROWS':
      return GalleryOptions_Layout_Orientation.ROWS;
    case 1:
    case 'COLUMNS':
      return GalleryOptions_Layout_Orientation.COLUMNS;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return GalleryOptions_Layout_Orientation.UNRECOGNIZED;
  }
}

export function galleryOptions_Layout_OrientationToJSON(
  object: GalleryOptions_Layout_Orientation
): string {
  switch (object) {
    case GalleryOptions_Layout_Orientation.ROWS:
      return 'ROWS';
    case GalleryOptions_Layout_Orientation.COLUMNS:
      return 'COLUMNS';
    default:
      return 'UNKNOWN';
  }
}

export interface GalleryOptions_ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number;
  /** Item ratio */
  ratio?: number;
  /** Sets how item images are cropped. */
  crop: GalleryOptions_ItemStyle_Crop;
  /** The spacing between items in pixels. */
  spacing?: number;
}

export function galleryOptions_ItemStyle_CropFromJSON(object: any): GalleryOptions_ItemStyle_Crop {
  switch (object) {
    case 0:
    case 'FILL':
      return GalleryOptions_ItemStyle_Crop.FILL;
    case 1:
    case 'FIT':
      return GalleryOptions_ItemStyle_Crop.FIT;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return GalleryOptions_ItemStyle_Crop.UNRECOGNIZED;
  }
}

export function galleryOptions_ItemStyle_CropToJSON(object: GalleryOptions_ItemStyle_Crop): string {
  switch (object) {
    case GalleryOptions_ItemStyle_Crop.FILL:
      return 'FILL';
    case GalleryOptions_ItemStyle_Crop.FIT:
      return 'FIT';
    default:
      return 'UNKNOWN';
  }
}

export interface GalleryOptions_Thumbnails {
  /** Thumbnail alignment. */
  placement: GalleryOptions_Thumbnails_Alignment;
  /** Spacing between thumbnails in pixels. */
  spacing?: number;
}

export function galleryOptions_Thumbnails_AlignmentFromJSON(
  object: any
): GalleryOptions_Thumbnails_Alignment {
  switch (object) {
    case 0:
    case 'TOP':
      return GalleryOptions_Thumbnails_Alignment.TOP;
    case 1:
    case 'RIGHT':
      return GalleryOptions_Thumbnails_Alignment.RIGHT;
    case 2:
    case 'BOTTOM':
      return GalleryOptions_Thumbnails_Alignment.BOTTOM;
    case 3:
    case 'LEFT':
      return GalleryOptions_Thumbnails_Alignment.LEFT;
    case 4:
    case 'NONE':
      return GalleryOptions_Thumbnails_Alignment.NONE;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return GalleryOptions_Thumbnails_Alignment.UNRECOGNIZED;
  }
}

export function galleryOptions_Thumbnails_AlignmentToJSON(
  object: GalleryOptions_Thumbnails_Alignment
): string {
  switch (object) {
    case GalleryOptions_Thumbnails_Alignment.TOP:
      return 'TOP';
    case GalleryOptions_Thumbnails_Alignment.RIGHT:
      return 'RIGHT';
    case GalleryOptions_Thumbnails_Alignment.BOTTOM:
      return 'BOTTOM';
    case GalleryOptions_Thumbnails_Alignment.LEFT:
      return 'LEFT';
    case GalleryOptions_Thumbnails_Alignment.NONE:
      return 'NONE';
    default:
      return 'UNKNOWN';
  }
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items: GalleryData_Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean;
}

export interface GalleryData_Item {
  /** An image item. */
  image?: GalleryData_Item_Image | undefined;
  /** A video item. */
  video?: GalleryData_Item_Video | undefined;
  /** Item title. */
  title?: string;
  /** Item's alternative text. */
  altText?: string;
}

export interface GalleryData_Item_Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface GalleryData_Item_Image {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

const baseGalleryOptions: object = {};

export const GalleryOptions = {
  fromJSON(object: any): GalleryOptions {
    const message = { ...baseGalleryOptions } as GalleryOptions;
    message.layout =
      object.layout !== undefined && object.layout !== null
        ? GalleryOptions_Layout.fromJSON(object.layout)
        : undefined;
    message.item =
      object.item !== undefined && object.item !== null
        ? GalleryOptions_ItemStyle.fromJSON(object.item)
        : undefined;
    message.thumbnails =
      object.thumbnails !== undefined && object.thumbnails !== null
        ? GalleryOptions_Thumbnails.fromJSON(object.thumbnails)
        : undefined;
    return message;
  },

  toJSON(message: GalleryOptions): unknown {
    const obj: any = {};
    message.layout !== undefined &&
      (obj.layout = message.layout ? GalleryOptions_Layout.toJSON(message.layout) : undefined);
    message.item !== undefined &&
      (obj.item = message.item ? GalleryOptions_ItemStyle.toJSON(message.item) : undefined);
    message.thumbnails !== undefined &&
      (obj.thumbnails = message.thumbnails
        ? GalleryOptions_Thumbnails.toJSON(message.thumbnails)
        : undefined);
    return obj;
  },
};

const baseGalleryOptions_Layout: object = {
  type: GalleryOptions_Layout_Type.COLLAGE,
  orientation: GalleryOptions_Layout_Orientation.ROWS,
};

export const GalleryOptions_Layout = {
  fromJSON(object: any): GalleryOptions_Layout {
    const message = { ...baseGalleryOptions_Layout } as GalleryOptions_Layout;
    message.type =
      object.type !== undefined && object.type !== null
        ? galleryOptions_Layout_TypeFromJSON(object.type)
        : GalleryOptions_Layout_Type.COLLAGE;
    message.horizontalScroll =
      object.horizontalScroll !== undefined && object.horizontalScroll !== null
        ? Boolean(object.horizontalScroll)
        : undefined;
    message.orientation =
      object.orientation !== undefined && object.orientation !== null
        ? galleryOptions_Layout_OrientationFromJSON(object.orientation)
        : GalleryOptions_Layout_Orientation.ROWS;
    message.numberOfColumns =
      object.numberOfColumns !== undefined && object.numberOfColumns !== null
        ? Number(object.numberOfColumns)
        : undefined;
    message.mobileNumberOfColumns =
      object.mobileNumberOfColumns !== undefined && object.mobileNumberOfColumns !== null
        ? Number(object.mobileNumberOfColumns)
        : undefined;
    return message;
  },

  toJSON(message: GalleryOptions_Layout): unknown {
    const obj: any = {};
    message.type !== undefined && (obj.type = galleryOptions_Layout_TypeToJSON(message.type));
    message.horizontalScroll !== undefined && (obj.horizontalScroll = message.horizontalScroll);
    message.orientation !== undefined &&
      (obj.orientation = galleryOptions_Layout_OrientationToJSON(message.orientation));
    message.numberOfColumns !== undefined && (obj.numberOfColumns = message.numberOfColumns);
    message.mobileNumberOfColumns !== undefined &&
      (obj.mobileNumberOfColumns = message.mobileNumberOfColumns);
    return obj;
  },
};

const baseGalleryOptions_ItemStyle: object = { crop: GalleryOptions_ItemStyle_Crop.FILL };

export const GalleryOptions_ItemStyle = {
  fromJSON(object: any): GalleryOptions_ItemStyle {
    const message = { ...baseGalleryOptions_ItemStyle } as GalleryOptions_ItemStyle;
    message.targetSize =
      object.targetSize !== undefined && object.targetSize !== null
        ? Number(object.targetSize)
        : undefined;
    message.ratio =
      object.ratio !== undefined && object.ratio !== null ? Number(object.ratio) : undefined;
    message.crop =
      object.crop !== undefined && object.crop !== null
        ? galleryOptions_ItemStyle_CropFromJSON(object.crop)
        : GalleryOptions_ItemStyle_Crop.FILL;
    message.spacing =
      object.spacing !== undefined && object.spacing !== null ? Number(object.spacing) : undefined;
    return message;
  },

  toJSON(message: GalleryOptions_ItemStyle): unknown {
    const obj: any = {};
    message.targetSize !== undefined && (obj.targetSize = message.targetSize);
    message.ratio !== undefined && (obj.ratio = message.ratio);
    message.crop !== undefined && (obj.crop = galleryOptions_ItemStyle_CropToJSON(message.crop));
    message.spacing !== undefined && (obj.spacing = message.spacing);
    return obj;
  },
};

const baseGalleryOptions_Thumbnails: object = {
  placement: GalleryOptions_Thumbnails_Alignment.TOP,
};

export const GalleryOptions_Thumbnails = {
  fromJSON(object: any): GalleryOptions_Thumbnails {
    const message = { ...baseGalleryOptions_Thumbnails } as GalleryOptions_Thumbnails;
    message.placement =
      object.placement !== undefined && object.placement !== null
        ? galleryOptions_Thumbnails_AlignmentFromJSON(object.placement)
        : GalleryOptions_Thumbnails_Alignment.TOP;
    message.spacing =
      object.spacing !== undefined && object.spacing !== null ? Number(object.spacing) : undefined;
    return message;
  },

  toJSON(message: GalleryOptions_Thumbnails): unknown {
    const obj: any = {};
    message.placement !== undefined &&
      (obj.placement = galleryOptions_Thumbnails_AlignmentToJSON(message.placement));
    message.spacing !== undefined && (obj.spacing = message.spacing);
    return obj;
  },
};

const baseGalleryData: object = {};

export const GalleryData = {
  fromJSON(object: any): GalleryData {
    const message = { ...baseGalleryData } as GalleryData;
    message.containerData =
      object.containerData !== undefined && object.containerData !== null
        ? PluginContainerData.fromJSON(object.containerData)
        : undefined;
    message.items = (object.items ?? []).map((e: any) => GalleryData_Item.fromJSON(e));
    message.options =
      object.options !== undefined && object.options !== null
        ? GalleryOptions.fromJSON(object.options)
        : undefined;
    message.disableExpand =
      object.disableExpand !== undefined && object.disableExpand !== null
        ? Boolean(object.disableExpand)
        : undefined;
    message.disableDownload =
      object.disableDownload !== undefined && object.disableDownload !== null
        ? Boolean(object.disableDownload)
        : undefined;
    return message;
  },

  toJSON(message: GalleryData): unknown {
    const obj: any = {};
    message.containerData !== undefined &&
      (obj.containerData = message.containerData
        ? PluginContainerData.toJSON(message.containerData)
        : undefined);
    if (message.items) {
      obj.items = message.items.map(e => (e ? GalleryData_Item.toJSON(e) : undefined));
    } else {
      obj.items = [];
    }
    message.options !== undefined &&
      (obj.options = message.options ? GalleryOptions.toJSON(message.options) : undefined);
    message.disableExpand !== undefined && (obj.disableExpand = message.disableExpand);
    message.disableDownload !== undefined && (obj.disableDownload = message.disableDownload);
    return obj;
  },
};

const baseGalleryData_Item: object = {};

export const GalleryData_Item = {
  fromJSON(object: any): GalleryData_Item {
    const message = { ...baseGalleryData_Item } as GalleryData_Item;
    message.image =
      object.image !== undefined && object.image !== null
        ? GalleryData_Item_Image.fromJSON(object.image)
        : undefined;
    message.video =
      object.video !== undefined && object.video !== null
        ? GalleryData_Item_Video.fromJSON(object.video)
        : undefined;
    message.title =
      object.title !== undefined && object.title !== null ? String(object.title) : undefined;
    message.altText =
      object.altText !== undefined && object.altText !== null ? String(object.altText) : undefined;
    return message;
  },

  toJSON(message: GalleryData_Item): unknown {
    const obj: any = {};
    message.image !== undefined &&
      (obj.image = message.image ? GalleryData_Item_Image.toJSON(message.image) : undefined);
    message.video !== undefined &&
      (obj.video = message.video ? GalleryData_Item_Video.toJSON(message.video) : undefined);
    message.title !== undefined && (obj.title = message.title);
    message.altText !== undefined && (obj.altText = message.altText);
    return obj;
  },
};

const baseGalleryData_Item_Video: object = {};

export const GalleryData_Item_Video = {
  fromJSON(object: any): GalleryData_Item_Video {
    const message = { ...baseGalleryData_Item_Video } as GalleryData_Item_Video;
    message.media =
      object.media !== undefined && object.media !== null
        ? Media.fromJSON(object.media)
        : undefined;
    message.thumbnail =
      object.thumbnail !== undefined && object.thumbnail !== null
        ? Media.fromJSON(object.thumbnail)
        : undefined;
    return message;
  },

  toJSON(message: GalleryData_Item_Video): unknown {
    const obj: any = {};
    message.media !== undefined &&
      (obj.media = message.media ? Media.toJSON(message.media) : undefined);
    message.thumbnail !== undefined &&
      (obj.thumbnail = message.thumbnail ? Media.toJSON(message.thumbnail) : undefined);
    return obj;
  },
};

const baseGalleryData_Item_Image: object = {};

export const GalleryData_Item_Image = {
  fromJSON(object: any): GalleryData_Item_Image {
    const message = { ...baseGalleryData_Item_Image } as GalleryData_Item_Image;
    message.media =
      object.media !== undefined && object.media !== null
        ? Media.fromJSON(object.media)
        : undefined;
    message.link =
      object.link !== undefined && object.link !== null ? Link.fromJSON(object.link) : undefined;
    return message;
  },

  toJSON(message: GalleryData_Item_Image): unknown {
    const obj: any = {};
    message.media !== undefined &&
      (obj.media = message.media ? Media.toJSON(message.media) : undefined);
    message.link !== undefined && (obj.link = message.link ? Link.toJSON(message.link) : undefined);
    return obj;
  },
};
